import type { MetricsSectionFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import Eyebrow from '../ui/Eyebrow'
import { TextWithHighlight } from '../TextWithHighlight'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import clsx from 'clsx'

export default function MetricsSection({
	tag,
	title,
	metrics,
	theme,
}: Readonly<MetricsSectionFragment>) {
	if (!metrics) return null
	return (
		<section
			className={clsx(
				'@container pb-10',
				theme === 'dark' ? 'bg-blue text-white' : 'bg-white text-blue-dark'
			)}
		>
			<div className="common-x-padding max-w-screen-2xl mx-auto py-20 remove-space-if-in-rich-text">
				{tag && <Eyebrow variant={theme === 'dark' ? 'secondary' : 'primary'}>{tag}</Eyebrow>}
				{title && !isStructuredTextEmpty(title.text) && (
					<h2 className="heading-size-2 max-w-xl mb-24 mt-10">
						<TextWithHighlight text={title} />
					</h2>
				)}

				<div className="grid grid-cols-1 @lg:grid-cols-2 @5xl:grid-cols-4 gap-y-16">
					{metrics.map(({ id, title: metricTitle, description, icon, amount, amountLabel }) => (
						<div key={id} className="flex flex-col border-l px-5">
							<div className="flex items-center gap-3">
								{icon && (
									<Image
										src={icon.url}
										alt={icon.alt ?? 'Metric Icon'}
										width={icon.width as number}
										height={icon.height as number}
										sizes="28px"
										className="w-8 h-8"
									/>
								)}
								{title && <h2 className="text-2xl">{metricTitle}</h2>}
							</div>
							{description && <p className="mt-4">{description}</p>}
							{amount && (
								<span
									className={clsx(
										'inline-block text-4xl @3xl:text-8xl pt-4 @3xl:pt-20 mt-auto',
										theme === 'dark' ? 'text-white' : 'text-blue'
									)}
								>
									{amount}
									{amountLabel}
								</span>
							)}
						</div>
					))}
				</div>
			</div>
		</section>
	)
}
